import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiClose, mdiMagnify } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover'
import { useDebounce } from '../../hooks/useDebounce'
import {
  EQUIPMENT_CONDITIONS,
  type EquipmentCondition,
} from '../../api/equipment-tab-types'

export interface FilterState {
  q: string
  conditions: EquipmentCondition[]
  trades: string[]
  /** Reserved — not wired in v0; placeholder for the location-tree picker. */
  locations?: string[]
}

interface EquipmentFilterBarProps {
  /** Current filter state (URL-synced upstream). */
  state: FilterState
  /** Called when any filter changes. The parent persists to the URL. */
  onChange: (next: FilterState) => void
}

/**
 * Sticky filter bar at the top of zone B (spec §6.6).
 *
 * The search box debounces user input (200 ms per spec) before bubbling up.
 * The condition multi-select uses a Popover/Checkbox pattern to stay in
 * the project's shadcn-on-Radix idiom (no toast, no native confirm).
 */
export function EquipmentFilterBar({
  state,
  onChange,
}: EquipmentFilterBarProps) {
  const { t } = useTranslation('assets')
  const [searchInput, setSearchInput] = useState(state.q)
  const debouncedSearch = useDebounce(searchInput, 200)

  // Forward debounced search to the parent only when the value actually
  // changes. This avoids re-emitting on every render.
  useEffect(() => {
    if (debouncedSearch !== state.q) {
      onChange({ ...state, q: debouncedSearch })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  // Re-sync local input if the URL state mutated externally.
  useEffect(() => {
    setSearchInput(state.q)
  }, [state.q])

  const toggleCondition = (c: EquipmentCondition) => {
    const set = new Set(state.conditions)
    if (set.has(c)) set.delete(c)
    else set.add(c)
    onChange({ ...state, conditions: Array.from(set) })
  }

  const isAnyFilterActive =
    state.q !== '' || state.conditions.length > 0

  const handleReset = () => {
    setSearchInput('')
    onChange({ q: '', conditions: [], trades: [] })
  }

  const conditionLabel =
    state.conditions.length === 0
      ? t('equipment_tab.filter.condition_all')
      : t('equipment_tab.filter.condition_n', {
          count: state.conditions.length,
        })

  return (
    <div
      className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border py-2 -mx-4 px-4 mb-3"
      data-testid="equipment-tab-zone-b-filter-bar"
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <AppIcon path={mdiMagnify} size="xs" className="absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('equipment_tab.filter.search_placeholder')}
            className="pl-7 h-8 text-sm"
            data-testid="equipment-tab-zone-b-filter-search"
          />
        </div>

        {/* Condition multi-select */}
        <Popover>
          <PopoverTrigger
            className="inline-flex items-center gap-1 h-8 px-2.5 text-xs border border-border rounded-md bg-background hover:bg-muted"
            data-testid="equipment-tab-zone-b-filter-condition"
          >
            {conditionLabel}
            <AppIcon path={mdiChevronDown} size="xs" tone="muted" />
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="text-[11px] text-muted-foreground uppercase font-semibold mb-1.5 tracking-wide">
              {t('equipment_tab.filter.condition_label')}
            </div>
            {EQUIPMENT_CONDITIONS.map((c) => (
              <label
                key={c}
                className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted cursor-pointer"
                data-testid={`equipment-tab-zone-b-filter-condition-${c}`}
              >
                <Checkbox
                  checked={state.conditions.includes(c)}
                  onCheckedChange={() => toggleCondition(c)}
                />
                <span className="text-xs">{t(`equipment_tab.condition.${c}`)}</span>
              </label>
            ))}
          </PopoverContent>
        </Popover>

        {/* Reset */}
        {isAnyFilterActive && (
          <button
            type="button"
            onClick={handleReset}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="equipment-tab-zone-b-filter-reset"
          >
            <AppIcon path={mdiClose} size="xs" />
            {t('equipment_tab.filter.reset')}
          </button>
        )}
      </div>
    </div>
  )
}
